import { useEffect, useState, useRef, useMemo } from 'react'
import { Image } from '@/components/common/image';
import { cn } from '@/lib/utils';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { AgentV2PushContentDetailData } from '@/services/agent-v2-push-content-detail';

/** 自动播放间隔（毫秒） */
const AUTO_PLAY_INTERVAL = 5000;

/** 左侧图文区域 */
export const LeftImage: React.FC<{ details: AgentV2PushContentDetailData }> = ({ details }) => {
  const [images, setImages] = useState(details?.imageUrls || []);

  const [activeIndex, setActiveIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const transitionLockRef = useRef(false);

  // 总轮播图数量
  const totalSlides = images.length;
  const isSingleSlide = totalSlides <= 1;

  useEffect(() => {
    setImages(details?.imageUrls || []);
  }, [details?.imageUrls])

  // 点击切换指示器
  const handleIndicatorClick = (index: number) => {
    setActiveIndex(index);
    resetAutoPlay();
  };

  const lockDuringTransition = () => {
    transitionLockRef.current = true;
    window.setTimeout(() => {
      transitionLockRef.current = false;
    }, 520);
  };

  const handlePrev = () => {
    if (transitionLockRef.current) return;
    lockDuringTransition();
    setActiveIndex((prev) => (prev - 1 + totalSlides) % totalSlides);
    resetAutoPlay();
  };

  const handleNext = () => {
    if (transitionLockRef.current) return;
    lockDuringTransition();
    // 允许走到克隆首图那一帧（activeIndex === totalSlides），但不允许继续超出导致空白
    setActiveIndex((prev) => (prev >= totalSlides ? totalSlides : prev + 1));
    resetAutoPlay();
  };

  // 启动自动播放
  const startAutoPlay = () => {
    // 只有在多张图片时才开启自动轮播
    if (isSingleSlide) return;
    stopAutoPlay();
    intervalRef.current = setInterval(() => {
      setActiveIndex((prev) => (prev >= totalSlides ? totalSlides : prev + 1));
    }, AUTO_PLAY_INTERVAL);
  };

  // 停止自动播放
  const stopAutoPlay = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
  };

  // 重置自动播放
  const resetAutoPlay = () => {
    stopAutoPlay();
    startAutoPlay();
  };

  // 处理无缝循环逻辑
  useEffect(() => {
    // 单张图片无需处理无缝循环
    if (isSingleSlide) return;

    if (activeIndex === totalSlides) {
      // 等过渡动画结束后瞬间重置
      const timeout = setTimeout(() => {
        setIsTransitioning(false);
        setActiveIndex(0);
      }, 500); // 与 CSS 动画时长一致
      return () => clearTimeout(timeout);
    } else {
      setIsTransitioning(true);
    }
  }, [activeIndex, totalSlides]);

  // 启动自动播放
  useEffect(() => {
    startAutoPlay();
    return () => stopAutoPlay();
  }, [isSingleSlide, totalSlides]);

  // 构造含克隆首图的数组（用于无缝滚动，单张图片则不克隆）
  const extendedImages = useMemo(
    () => (isSingleSlide ? images : [...images, images[0]]),
    [images, isSingleSlide]
  );

  return (
    <div className='flex-1 h-full relative bg-white flex flex-col border-r border-r-[rgba(233,234,235,1)]'>
      <div className='relative w-full group' style={{ height: '387px' }}>
        {/* 轮播图容器 */}
        <div className="w-full h-full bg-black overflow-hidden relative select-none">
          <div
            className={cn("flex flex-row h-full", isTransitioning && "transition-transform duration-500 ease-in-out")}
            style={{
              width: `${extendedImages.length * 100}%`,
              transform: `translateX(-${activeIndex * (100 / extendedImages.length)}%)`,
            }}
          >
            {extendedImages.map((src, index) => (
              <div
                key={index}
                className="flex-shrink-0 flex items-center justify-center h-full"
                style={{ width: `${100 / extendedImages.length}%` }}
              >
                <Image
                  src={src}
                  alt={`Slide ${index + 1}`}
                  className="object-cover"
                  style={{ width: '290px', height: '387px' }}
                />
              </div>
            ))}
          </div>
        </div>

        {/* 右上角数量 */}
        <div className='absolute top-6 right-5 text-white text-sm font-medium'>
          {totalSlides ? (activeIndex % totalSlides) + 1 : 0}/{totalSlides}
        </div>

        {/* 指示器 */}
        {!isSingleSlide && <div className="absolute bottom-4 left-0 right-0 flex justify-center space-x-2">
          {images.map((_, index) => (
            <div
              key={index}
              className={cn(
                'w-2 h-2 rounded-full transition-all duration-300 cursor-pointer',
                index === (activeIndex % totalSlides)
                  ? 'bg-white'
                  : 'bg-[rgba(255,255,255,0.6)] hover:bg-white/80'
              )}
              onClick={() => handleIndicatorClick(index)}
            ></div>
          ))}
        </div>}

        {/* 左右切换按钮（快速切换下，会出现展示空白的情况） */}
        {!isSingleSlide && (
          <>
            <button
              onClick={handlePrev}
              className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-black/30 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 hidden md:block"
              aria-label="Previous slide"
            >
              <ChevronLeftIcon className="h-6 w-6" />
            </button>
            <button
              onClick={handleNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-black/30 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 hidden md:block"
              aria-label="Next slide"
            >
              <ChevronRightIcon className="h-6 w-6" />
            </button>
          </>
        )}
      </div>

      {/* 文案区 */}
      <div className='flex-1 px-6 pr-4 my-5 overflow-y-auto pb-6'>
        <h2 className='text-[16px] font-medium text-[rgba(16,24,40,1)] mb-4 line-clamp-2'>
          {details?.contentTitle || ''}
        </h2>
        <div
          className='w-full text-[14px] text-[rgba(16,24,40,1)] space-y-4'
        >
          <div className='whitespace-pre-line'>{details?.content}</div>
        </div>
      </div>
    </div>
  )
}
